/**
 * GroupBannerUpload component.
 * Displays a rectangular group banner with an overlay that lets users
 * pick and upload a new banner image to Supabase Storage.
 */

import { useRef, useState } from 'react';
import { supabase } from '../utils/supabase';
import '../styles/GroupBannerUpload.css';

interface GroupBannerUploadProps {
  currentUrl: string | null;
  groupId: string;
  onUploadComplete: (url: string) => void;
  aspectRatio?: string; // e.g. "16 / 9"
}

export default function GroupBannerUpload({
  currentUrl,
  groupId,
  onUploadComplete,
  aspectRatio = '16 / 9',
}: GroupBannerUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleBoxClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Basic validation
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image (PNG, JPEG, WebP, GIF).');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be smaller than 5MB.');
      return;
    }

    try {
      setIsUploading(true);
      setError(null);

      const ext = file.name.split('.').pop();
      const filePath = `${groupId}/banner.${ext}`;

      // Upload to 'groups' bucket
      const { data, error: uploadError } = await supabase.storage
        .from('groups')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage.from('groups').getPublicUrl(data.path);

      // Append timestamp to prevent showing caching of the old image
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      onUploadComplete(publicUrl);
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="group-banner-upload-wrapper">
      <h3 className="group-banner-title">Banner Image</h3>

      <div
        className={`group-banner-container ${isUploading ? 'uploading' : ''}`}
        style={{ aspectRatio }}
        onClick={handleBoxClick}
      >
        {currentUrl ? (
          <img
            onLoad={(e) => e.currentTarget.classList.add('loaded')}
            src={currentUrl}
            alt="Group Banner"
            className="group-banner-img"
          />
        ) : (
          <div className="group-banner-placeholder">
            <span>Click to upload banner</span>
          </div>
        )}

        <div className="group-banner-overlay">
          <div className="group-banner-overlay-content">
            {/* SVG stays same */}
            <span>Change Banner</span>
          </div>
        </div>

        {isUploading && (
          <div className="group-banner-loading">
            <div className="group-banner-spinner"></div>
          </div>
        )}
      </div>

      {error && <p className="group-banner-error">{error}</p>}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: 'none' }}
      />
    </div>
  );
}
